import { useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { map, isFunction } from "lodash"
import { useDispatch } from "react-redux"
import { Creators } from "model/apiWrapper"
import { useAccountAlias } from "resources/account"
import { combineTanStackStatuses, ERROR, OK } from "helpers/data-statuses"
import useCombinedQueries from "hooks/combine-queries"
import { useIntradayEnhancement } from "hooks/use-intraday-enhancement"
import { extractFirstGraphqlErrorMessage, rejectOnGraphqlErrors } from "./extract-graphql-error-message"

export default function DataIntegrator({ metrics, Renderer, rendererProps, dataTransform }) {
  const dispatch = useDispatch()
  const account = useAccountAlias()
  const combine = useCallback(results => {
    const status = combineTanStackStatuses(results)
    const data = status === OK ? map(results, "data") : null
    const errorMessage = status === ERROR ? extractFirstGraphqlErrorMessage(results) : null
    return { status, data, errorMessage }
  }, [])

  const queries = useMemo(() => map(metrics, ({ key, query, variables, select }) => ({
    queryKey: key,
    queryFn: ({ signal }) => new Promise((resolve, reject) => {
      dispatch(Creators.graphql(account, query, variables, rejectOnGraphqlErrors(resolve, reject), reject, signal))
    }),
    select,
  })), [metrics, account, dispatch])

  const { status, data, errorMessage } = useCombinedQueries(queries, combine)
  const { effectiveData, effectiveStatus } = useIntradayEnhancement(data, status, queries.map(q => q.queryKey))
  const transformedData = useMemo(() => {
    if (effectiveStatus === OK && isFunction(dataTransform)) {
      return dataTransform(effectiveData)
    }
    return effectiveData
  }, [effectiveStatus, effectiveData, dataTransform])

  return <Renderer status={effectiveStatus} data={transformedData} errorMessage={errorMessage} {...rendererProps} />
}

DataIntegrator.propTypes = {
  metrics: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.arrayOf(PropTypes.string).isRequired,
    query: PropTypes.string.isRequired,
    variables: PropTypes.object.isRequired,
    select: PropTypes.func.isRequired,
  })).isRequired,
  Renderer: PropTypes.func.isRequired,
  rendererProps: PropTypes.object,
  dataTransform: PropTypes.func,
}
